import { useRef, useEffect } from "react";
import { Brain, Maximize2, Minus, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";
import { BrainErrorState, type BrainErrorType } from "./BrainErrorState";
import type { ChatMessage } from "@/hooks/useChatHistory";
import type { PageContext } from "@/hooks/usePageContext";

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  pageContext: PageContext;
  onSend: (text: string) => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onClose: () => void;
  onClear: () => void;
  onActionClick?: (actionKey: string) => void;
  brainError?: { type: BrainErrorType; waitMinutes?: number } | null;
  onRetry?: () => void;
}

export function ChatDocked({
  messages,
  loading,
  pageContext,
  onSend,
  onMinimize,
  onMaximize,
  onClose,
  onClear,
  onActionClick,
  brainError,
  onRetry,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="fixed bottom-0 right-0 z-40 w-full md:w-[360px] max-w-[calc(100vw-2rem)] flex flex-col rounded-t-xl md:rounded-xl border border-border bg-card shadow-2xl overflow-hidden md:right-4 md:bottom-4 md:max-w-[360px] md:h-[480px] h-[60dvh] max-h-[calc(100dvh-6rem)]">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card min-w-0">
        <div className="rounded-md bg-primary/10 p-1.5">
          <Brain className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-display font-semibold text-foreground">
            Cerveau Central
          </span>
          <span className="text-[10px] text-muted-foreground ml-1">
            — {pageContext.page}
          </span>
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClear} title="Effacer">
          <Trash2 className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMinimize} title="Réduire">
          <Minus className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMaximize} title="Agrandir">
          <Maximize2 className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose} title="Fermer">
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Messages */}
      {messages.length === 0 && !loading ? (
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <Brain className="h-8 w-8 text-primary/30 mb-2" />
          <p className="text-xs text-muted-foreground max-w-[240px]">
            Pose une question basée sur les faits validés et ton profil sportif.
          </p>
          {pageContext.suggested_actions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
              {pageContext.suggested_actions.map((a) => (
                <button
                  key={a.label}
                  onClick={() => a.query && onSend(a.query)}
                  disabled={!a.query}
                  className="text-[10px] px-2 py-1 rounded-md border border-border bg-secondary/50 text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors"
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <ChatMessages
          ref={scrollRef}
          messages={messages}
          loading={loading}
          compact
          onExpandRequest={onMaximize}
          onActionClick={onActionClick}
        />
      )}

      {/* Error state */}
      {brainError && (
        <div className="border-t border-border px-3 py-2">
          <BrainErrorState
            errorType={brainError.type}
            waitMinutes={brainError.waitMinutes}
            onRetry={onRetry}
            inline
          />
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-2">
        <ChatInput loading={loading} onSend={onSend} compact />
      </div>
    </div>
  );
}
